import {
  Box,
  Progress,
  Group,
  Text,
  Center,
  PasswordInput,
  Container,
  Anchor,
  PasswordInputProps,
} from "@mantine/core";
import { IconCheck, IconExternalLink, IconX } from "@tabler/icons-react";
import React from "react";
import { forwardRef } from "react";

const PasswordRequirement = ({
  meets,
  label,
}: {
  meets: boolean;
  label: string;
}) => {
  return (
    <Text color={meets ? "teal" : "red"} mt={5} size="sm">
      <Center inline>
        {meets ? (
          <IconCheck size="0.9rem" stroke={1.5} />
        ) : (
          <IconX size="0.9rem" stroke={1.5} />
        )}
        <Box ml={7}>{label}</Box>
      </Center>
    </Text>
  );
};

export interface IPasswordPositiveRequirement {
  re: RegExp;
  label: string;
}

export interface IPasswordNegativeRequirement {
  condition: (value: string, options: IPasswordStrengthOptions) => boolean;
  label: string;
}

const getStrength = (
  lengthMet: boolean,
  positiveCheckResults: { met: boolean }[],
  negativeCheckResults: { met: boolean }[],
  options: IPasswordStrengthOptions,
) => {
  const matchedRules = Math.max(
    (lengthMet ? 1 : 0) +
      Math.min(
        positiveCheckResults.filter((r) => r.met).length,
        options.minPositiveRules,
      ) -
      negativeCheckResults.filter((r) => r.met).length,
    0,
  );

  return (100 / (options.minPositiveRules + 1)) * matchedRules;
};

export interface IPasswordStrengthOptions {
  minPositiveRules: number;
  minLength: number;
  userFields?: string[];
}

export interface IPasswordStrengthProps extends PasswordInputProps {
  options: IPasswordStrengthOptions;
  value?: string;
  positiveRequirements: IPasswordPositiveRequirement[];
  negativeRequirements: IPasswordNegativeRequirement[];
  additionalRestrictions?: React.ReactNode;
}

export const PasswordStrength = forwardRef<
  HTMLInputElement,
  IPasswordStrengthProps & React.RefAttributes<HTMLInputElement>
>((props, ref) => {
  const {
    options,
    value,
    positiveRequirements,
    negativeRequirements,
    additionalRestrictions,
    ...rest
  } = props;
  const testValue = value || "";
  const positiveCheckResults = positiveRequirements.map(({ re, label }) => ({
    label: label,
    met: re.test(testValue),
  }));
  const negativeCheckResults = negativeRequirements.map(
    ({ condition, label }) => ({
      label: label,
      met: condition(testValue, options),
    }),
  );

  const lengthMet = testValue.length >= options.minLength;

  const strength = getStrength(
    lengthMet,
    positiveCheckResults,
    negativeCheckResults,
    options,
  );

  const positiveChecks = positiveCheckResults.map(({ met, label }, index) => (
    <PasswordRequirement key={index} label={label} meets={met} />
  ));
  const negativeChecks = negativeCheckResults.map(({ met, label }, index) => (
    <PasswordRequirement key={index} label={label} meets={!met} />
  ));

  const bars = Array(options.minPositiveRules + 1)
    .fill(0)
    .map((_, index) => (
      <Progress
        styles={{ bar: { transitionDuration: "0ms" } }}
        value={
          testValue.length > 0 && index === 0
            ? 100
            : strength >= ((index + 1) / (options.minPositiveRules + 1)) * 100
              ? 100
              : 0
        }
        color={strength > 80 ? "teal" : strength > 50 ? "yellow" : "red"}
        key={index}
        size={options.minPositiveRules + 1}
      />
    ));

  return (
    <div>
      <PasswordInput value={value} ref={ref} {...rest} />

      <Group spacing={5} grow mt="xs" mb="md">
        {bars}
      </Group>

      <PasswordRequirement
        label={`Must be ${options.minLength} or more characters in length`}
        meets={lengthMet}
      />
      <PasswordRequirement
        label={`At least ${options.minPositiveRules} of:`}
        meets={
          positiveCheckResults.filter((r) => r.met).length >=
          options.minPositiveRules
        }
      />
      <Container ml="md">{positiveChecks}</Container>
      {negativeChecks}
      {additionalRestrictions}
    </div>
  );
});
